import { useMutation } from '@apollo/client'
import { Table, TableBody, TableCell, TableRow, TextField } from '@mui/material'
import { Formik } from 'formik'
import * as Yup from 'yup'

import { useState } from 'react'

import {
  FloraTypography,
  ModalBody,
  ModalFooter,
  ModalHeader,
  FloraButton,
  Box,
  Flex
} from '@grupoboticario/flora-react'

import { CurrentMovement } from '@/components/ExtractPoints/ExtractPoints.types'
import FormField from '@/components/FormField/FormField'
import ActionModal from '@/components/Modal/ActionModal'
import BackofficeTableContainer from '@/components/Table/Generic/BackofficeTableContainer/BackofficeTableContainer'
import BackofficeTablePagination from '@/components/Table/Generic/BackofficeTablePagination/BackofficeTablePagination'
import ExtractFields from '@/components/Table/Generic/ExtractFields/ExtractFields'
import NumberTextWithColor from '@/components/Table/Generic/NumberTextWithColor/NumberTextWithColor'
import TableHeader from '@/components/Table/Generic/TableHeader/TableHeader'
import TableHeaderActions from '@/components/Table/Generic/TableHeaderActions/TableHeaderActions'
import { MOVEMENT_TYPE } from '@/constants/movementType'
import { useHeaderContext } from '@/context/header'
import { useUserContext } from '@/context/user'
import MANUAL_ADJUSTMENT from '@/graphql/mutation/manualAdjustment'
import { PlusCircle } from '@/icons/PlusCircle'
import { BackofficeTableStyle } from '@/utils/customStyle/tableStyle'
import { Actions } from '@/utils/dictionaries/actionsDictionary/actions'
import { getTomorrowDateString } from '@/utils/format/parseDatetime'
import { getLimitValuePoints } from '@/utils/getLimitValuePoints'
import { columns } from '@/utils/tableColumns/extractPointsTable'
import triggerToastSuccess from '@/utils/triggerToastSuccess/triggerToastSuccess'
import { deactivateInput } from '@/utils/validators/deactivateUserInputs'

import * as S from './ExtractPointsTable.styles'

const ExtractPointsTable = ({
  data,
  total = 0,
  filter = {},
  loading,
  isTesting = false,
  onPaginationChange = () => {},
  onManualAdjustment = () => {},
  ...props
}) => {
  const [modalPoints, setModalPoints] = useState(isTesting)
  const [event, setEvent] = useState(null)
  const { user: loggedUser, hasPermission } = useUserContext()
  const { member, memberStatus } = useHeaderContext()
  const [manualAdjustment, { loading: loadingAdjustment }] = useMutation(MANUAL_ADJUSTMENT, {
    onError: () => {}
  })

  const pointsLimit = getLimitValuePoints(hasPermission(Actions.UPDATE_POINTS_UNLIMITED))

  const onManualAdjustmentEvent = async (values) => {
    const { error, errors } = await manualAdjustment({
      variables: {
        manualAdjustment: {
          origin: {
            channel: 'backoffice',
            originId: '-',
            identifier: loggedUser?.name || ''
          },
          amountPoint: values?.points || 0,
          event: values?.justification,
          expirationDate: values?.points >= 0 ? values?.pointsExpirationDate || '' : undefined,
          documentNumber: member?.documentNumber || '',
          movementType: 'MANUAL_ADJUSTMENT'
        }
      }
    })

    setModalPoints(false)

    triggerToastSuccess(
      error || errors,
      `Ajuste feito com sucesso! O processamento das alterações pode levar até 5 minutos para ser concluído, por favor, aguarde.`,
      () => {
        onManualAdjustment()
      },
      !isTesting
    )
  }

  return (
    <Box {...props}>
      <BackofficeTableStyle />
      <TableHeaderActions
        onActionClick={() => setModalPoints(true)}
        rightActions={[
          {
            event: 'add',
            text: 'Adicionar ou remover pontos',
            icon: <PlusCircle />,
            isDisabled: deactivateInput(hasPermission(Actions.UPDATE_POINTS), memberStatus?.status)
          }
        ]}
      />
      <BackofficeTableContainer columns={columns} loading={loading}>
        <Table aria-label="Tabela de extrato">
          <TableHeader columns={columns} />
          <TableBody>
            {data?.map((row: CurrentMovement) => {
              const isFutureEvent =
                row?.movementType !== MOVEMENT_TYPE.CASHBACK && row?.availableFrom
                  ? new Date(row.availableFrom) > new Date()
                  : false

              return (
                <TableRow
                  key={`${row?.createdAt}${row?.movementId}`}
                  style={{
                    fontStyle: isFutureEvent ? 'italic' : 'normal'
                  }}
                >
                  <ExtractFields row={row} isFutureEvent={isFutureEvent} setEvent={setEvent} />
                  <TableCell>
                    <NumberTextWithColor value={row?.accumulatedPoint}>{row?.accumulatedPoint}</NumberTextWithColor>
                  </TableCell>
                  <TableCell>
                    <FloraTypography fontSize="bodySmallStandard">{row?.origin?.originId || '-'}</FloraTypography>
                  </TableCell>
                  <TableCell>
                    <FloraTypography fontSize="bodySmallStandard">{row?.origin?.identifier || '-'}</FloraTypography>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </BackofficeTableContainer>
      <BackofficeTablePagination filter={filter} onPaginationChange={onPaginationChange} total={total} />
      <ActionModal css={S.modalWrapperCss} onOpenChange={setModalPoints} open={modalPoints} size="standard">
        <Formik
          initialValues={{
            pointsExpirationDate: '',
            justification: '',
            points: 0
          }}
          onSubmit={() => {}}
          validateOnMount
          validationSchema={Yup.object().shape({
            pointsExpirationDate: Yup.date().when('points', (points, schema) =>
              points >= 0
                ? schema
                    .required('Preencha com uma data de expiração!')
                    .transform((value, original) => new Date(original))
                    .min(getTomorrowDateString(), 'A expiração dos pontos precisa ser de no mínimo 1 dia')
                : schema
            ),
            justification: Yup.string().required('Preencha com uma justificativa!'),
            points: Yup.number()
              .required('Por favor, informe a quantidade de pontos.')
              .integer('O número não pode conter decimais.')
              .max(pointsLimit, `Permitido adicionar até ${pointsLimit} pontos.`)
          })}
        >
          {({ errors, values, setFieldValue, isValid }) => (
            <>
              <ModalHeader>Cadastrar pontuação</ModalHeader>
              <ModalBody>
                <Flex direction="column" gap="$4">
                  <FormField
                    data-testid="points"
                    invalid={typeof errors?.points === 'string'}
                    invalidText={errors.points}
                    labelText="Pontuação:"
                    name="points"
                    required
                    type="number"
                  />
                  <Box>
                    <FormField
                      data-testid="pointsExpirationDate"
                      invalid={typeof errors?.pointsExpirationDate === 'string'}
                      invalidText={errors.pointsExpirationDate}
                      labelText="Data de expiração:"
                      min={getTomorrowDateString()}
                      name="pointsExpirationDate"
                      required
                      type="date"
                    />
                  </Box>
                  <TextField
                    id="justification-text"
                    inputProps={{
                      'data-testid': 'justification'
                    }}
                    label="Justificativa"
                    minRows={3}
                    multiline
                    onChange={(e) => setFieldValue('justification', e.target.value)}
                    required
                    style={{ width: '100%' }}
                    variant="outlined"
                  />
                </Flex>
              </ModalBody>
              <ModalFooter>
                <FloraButton
                  disabled={!isValid}
                  isLoading={loadingAdjustment}
                  onClick={() => onManualAdjustmentEvent(values)}
                >
                  Salvar
                </FloraButton>
              </ModalFooter>
            </>
          )}
        </Formik>
      </ActionModal>
      <ActionModal onOpenChange={setEvent} open={Boolean(event)} size="standard">
        <ModalHeader>
          <FloraTypography as="h5" fontSize="bodyLargeStandard" fontWeight="medium">
            Justificativa
          </FloraTypography>
        </ModalHeader>
        <ModalBody css={S.modalBodyCss}>
          <FloraTypography fontSize="bodySmallStandard">{event}</FloraTypography>
        </ModalBody>
      </ActionModal>
    </Box>
  )
}

ExtractPointsTable.defaultProps = {
  filter: {},
  total: 0,
  loading: false,
  isTesting: false,
  onPaginationChange: () => {},
  onManualAdjustment: () => {}
}

export default ExtractPointsTable
